/* eslint-disable max-len */
import { Suspense } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Router } from 'react-router-dom';
import { styled } from '@mui/system';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// to access the history in the HTTP file we need to creat a 'custom' history object which is then passed as a
// history-prop to the Router-Component.
import browserHistory from 'src/services/routing/history';
import appRoutes from 'src/services/routing/routes';
import AtomProvider from 'src/components/app/context/AtomProvider';
import { BusinessUnitsProvider } from 'src/components/app/context/BusinessUnitsContext';
import ConfirmProvider from 'src/components/app/context/ConfirmContext';
import { CustomizingProvider } from 'src/components/app/context/CustomizingContext';
import { FilterProvider } from 'src/components/app/context/FilterContext';
import LocalizedThemeProvider from 'src/components/app/context/LocalizedThemeProvider';
import MaintenanceMessageProvider from 'src/components/app/context/MaintenanceMessageContext';
import { ModuleItemsProvider } from 'src/components/app/context/ModuleItemsContext';
import SnackbarProvider from 'src/components/app/context/SnackbarContext';
import UserProvider from 'src/components/app/context/UserContext';
import LoadingPage from 'src/components/app/LoadingPage';
import RoutesHandler from 'src/components/app/RoutesHandler';
import { DownloadManagerProvider } from 'src/components/downloadManager/DownloadManagerContext';
import PlanActionProvider from 'src/components/planAction/PlanActionContext';
import WorkspaceProvider from 'src/components/workspace/WorkspaceContext';

const StyledSnackbarProvider = styled(SnackbarProvider)({
  variantWarning: {
    backgroundColor: 'orange !important', // <-- note !important
  },
  containerRoot: {
    paddingTop: 10,
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 60000,
    },
  },
});

// "Router" must be first provider. See https://github.com/mui/material-ui/issues/14077
const App = () => (
  <>
    <Router history={browserHistory}>
      <Suspense fallback={<LoadingPage />}>
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <AtomProvider>
              <BusinessUnitsProvider>
                <CustomizingProvider>
                  <LocalizedThemeProvider>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <ConfirmProvider>
                        <WorkspaceProvider>
                          <FilterProvider>
                            <PlanActionProvider>
                              <DndProvider backend={HTML5Backend}>
                                <StyledSnackbarProvider maxSnack={10}>
                                  <MaintenanceMessageProvider>
                                    <DownloadManagerProvider>
                                      <ModuleItemsProvider>
                                        <RoutesHandler routes={appRoutes} />
                                      </ModuleItemsProvider>
                                    </DownloadManagerProvider>
                                  </MaintenanceMessageProvider>
                                </StyledSnackbarProvider>
                              </DndProvider>
                            </PlanActionProvider>
                          </FilterProvider>
                        </WorkspaceProvider>
                      </ConfirmProvider>
                    </LocalizationProvider>
                  </LocalizedThemeProvider>
                </CustomizingProvider>
              </BusinessUnitsProvider>
            </AtomProvider>
          </UserProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Suspense>
    </Router>
  </>
);

export default App;
